import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Loader2, Eye, EyeOff, Upload } from "lucide-react";
import api, { imgUrl } from "@/lib/api";

const empty = { title: "", excerpt: "", content: "", cover_url: "", published: true };

export default function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const coverRef = useRef();

  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setModal(true); };
  const openEdit = (p) => { setEditing(p); setForm({ title: p.title, excerpt: p.excerpt || "", content: p.content || "", cover_url: p.cover_url || "", published: p.published }); setModal(true); };

  const onCover = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("seo_name", f.name.replace(/\.[^.]+$/, ""));
    fd.append("alt_text", form.title || f.name.replace(/\.[^.]+$/, ""));
    try {
      const r = await api.post("/admin/media", fd);
      setForm((s) => ({ ...s, cover_url: r.data.image_url }));
      toast.success("Image de couverture importée (compressée en WebP)");
    } catch { toast.error("Échec de l'import de l'image"); }
    finally { setUploading(false); if (coverRef.current) coverRef.current.value = ""; }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Titre requis");
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/blog/${editing.id}`, form);
      else await api.post("/admin/blog", form);
      toast.success(editing ? "Article mis à jour" : "Article créé");
      setModal(false); load();
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    await api.delete(`/admin/blog/${id}`); load();
  };

  const inputCls = "w-full bg-zinc-900 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder-zinc-500 focus:border-yellow-400 focus:outline-none";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Blog</h1>
          <p className="text-zinc-500 text-sm mt-1">{posts.length} articles</p>
        </div>
        <button data-testid="blog-add" onClick={openNew} className="flex items-center gap-2 bg-yellow-400 text-black font-bold px-4 py-2.5 rounded-md hover:bg-yellow-300">
          <Plus className="w-4 h-4" /> Nouvel article
        </button>
      </div>

      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.id} data-testid="admin-blog-row" className="flex items-center gap-4 bg-[#18181B] border border-white/5 rounded-xl p-4">
            <div className="w-20 h-14 rounded-lg overflow-hidden bg-zinc-800 shrink-0">
              {p.cover_url && <img src={imgUrl(p.cover_url)} alt={p.title} className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{p.title}</div>
              <div className="text-xs text-zinc-500 truncate">{p.excerpt}</div>
            </div>
            <span className={`text-xs px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${p.published ? "bg-green-400/15 text-green-400" : "bg-zinc-700 text-zinc-300"}`}>
              {p.published ? <Eye size={12} /> : <EyeOff size={12} />} {p.published ? "Publié" : "Brouillon"}
            </span>
            <button data-testid="blog-edit" onClick={() => openEdit(p)} className="p-2 rounded-md bg-white/5 hover:bg-yellow-400 hover:text-black"><Pencil className="w-4 h-4" /></button>
            <button data-testid="blog-delete" onClick={() => remove(p.id)} className="p-2 rounded-md bg-white/5 hover:bg-red-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6 overflow-y-auto" onClick={() => setModal(false)}>
          <div className="bg-[#18181B] border border-white/10 rounded-2xl w-full max-w-2xl p-6 my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-xl font-bold">{editing ? "Modifier l'article" : "Nouvel article"}</h2>
              <button onClick={() => setModal(false)}><X className="w-5 h-5 text-zinc-400" /></button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <input data-testid="blog-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre de l'article" className={inputCls} />
              <div>
                <div onClick={() => coverRef.current?.click()} className="cursor-pointer border-2 border-dashed border-white/15 rounded-xl h-40 flex flex-col items-center justify-center overflow-hidden bg-white/5 hover:border-yellow-400 transition-colors">
                  {uploading ? <Loader2 className="w-6 h-6 text-yellow-400 animate-spin" /> : form.cover_url ? (
                    <img src={imgUrl(form.cover_url)} alt="couverture" className="h-full w-full object-cover" />
                  ) : (
                    <><Upload className="w-6 h-6 text-zinc-500 mb-2" /><span className="text-sm text-zinc-500">Cliquez pour choisir l'image de couverture</span></>
                  )}
                </div>
                <input ref={coverRef} data-testid="blog-cover-file" type="file" accept="image/*" onChange={onCover} className="hidden" />
                <input data-testid="blog-cover" value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} placeholder="…ou collez une URL d'image" className={`${inputCls} mt-2`} />
              </div>
              <textarea data-testid="blog-excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} placeholder="Résumé (extrait)" rows={2} className={inputCls} />
              <textarea data-testid="blog-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Contenu (HTML autorisé : <h2>, <p>…)" rows={10} className={inputCls} />
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input data-testid="blog-published" type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-yellow-400 w-4 h-4" />
                Publier immédiatement
              </label>
              <button data-testid="blog-save" disabled={saving} className="w-full flex items-center justify-center gap-2 bg-yellow-400 text-black font-bold py-3 rounded-md hover:bg-yellow-300 disabled:opacity-60">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
